import { supabase } from '../lib/supabase';
import {
  safeAuthorizedListingAssetUrl,
  safeExternalListingContentUrl,
} from './listingContentUrlPolicy';

export type ListingContentKind = 'course' | 'digital' | 'virtual';

export type ListingContentAsset = {
  id: string;
  key: string;
  name: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  purpose: string;
};

export type ListingContentManifestResponse = {
  listing: Record<string, any>;
  access: { source: 'owner' | 'direct' | 'bundle' | 'all_access'; orderId: string | null };
  manifest: {
    id: string | null;
    kind: ListingContentKind;
    schemaVersion: number;
    payload: Record<string, any>;
    updatedAt?: string;
  };
  assets: ListingContentAsset[];
};

export type ListingContentLibraryProduct = {
  listingId: string;
  accessSource: 'direct' | 'bundle' | 'all_access';
  orderId: string;
  listing: Record<string, any>;
};

const invoke = async <T>(body: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.functions.invoke('listing-content-access', { body });
  if (error) {
    if (error.context instanceof Response) {
      const payload = await error.context.clone().json().catch(() => null);
      if (typeof payload?.error === 'string') throw new Error(payload.error);
      if (typeof payload?.error?.message === 'string') throw new Error(payload.error.message);
    }
    throw error;
  }
  if (!data || typeof data !== 'object') throw new Error('LISTING_CONTENT_RESPONSE_EMPTY');
  if ('error' in data && data.error) throw new Error(String(data.error));
  return data as T;
};

export const getListingContentLibrary = async () => {
  const response = await invoke<{ products: ListingContentLibraryProduct[] }>({ action: 'library' });
  return response.products || [];
};

export const getListingContentManifest = async (listingId: string) => {
  const response = await invoke<ListingContentManifestResponse>({ action: 'manifest', listingId });
  // Public presentation media belongs to the listing, outside its protected downloads.
  response.manifest.payload = {
    ...response.manifest.payload,
    ...(response.listing.image_url ? { coverUrl: response.listing.image_url } : {}),
    ...(response.listing.video_url ? { trailerUrl: response.listing.video_url } : {}),
  };
  return response;
};

export const getListingContentAssetUrl = async (assetId: string, download = false) => {
  const response = await invoke<{ url: string; expiresIn: number }>({
    action: 'asset-url',
    assetId,
    download,
  });
  const url = safeAuthorizedListingAssetUrl(response.url);
  if (!url) throw new Error('UNSAFE_LISTING_ASSET_URL');
  return { ...response, url };
};

export const saveListingContentManifest = async (
  listingId: string,
  kind: ListingContentKind,
  payload: Record<string, unknown>,
) => {
  const { coverUrl: _cover, trailerUrl: _trailer, ...protectedPayload } = payload;
  return invoke<{
  manifest: ListingContentManifestResponse['manifest'];
  catalogMetadata: Record<string, unknown>;
}>({ action: 'save-manifest', listingId, kind, payload: protectedPayload });
};

export const claimFreeListing = (listingId: string) =>
  invoke<{ orderId: string; existing: boolean }>({ action: 'free-claim', listingId });

export const listingAssetId = (value: unknown) => {
  if (typeof value !== 'string') return null;
  return value.match(/^asset:\/\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i)?.[1] || null;
};

export const resolveListingAssetReference = async (value: unknown, download = false) => {
  const assetId = listingAssetId(value);
  if (!assetId) {
    const externalUrl = safeExternalListingContentUrl(value);
    if (!externalUrl) throw new Error('UNSAFE_LISTING_CONTENT_REFERENCE');
    return externalUrl;
  }
  const response = await getListingContentAssetUrl(assetId, download);
  return response.url;
};

export const privateListingObjectReference = (objectId: string) => `wersee-object://${objectId}`;
